import { CloseIcon } from '../components/icons';
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { deliverableTemplatesApi } from '../utils/api';

// Outcome Setup Redesign WU 1-13.
//
// Replaces OutcomeTypes.js. Card grid of Deliverable Templates from the
// caller's workspace (via WU 1-4 API). Filter by category, sort toggle
// between recency and use count. First-time banner explains the move
// from "Outcome Types" → "Deliverable Templates" for returning users.
//
// Per design-spec Open Question #6: edits to a template only affect
// future Proposals; no live link to past Deliverables. Editing opens
// a side panel with the template's name + spec block content as
// editable markdown textareas (PRD-003 §4.1 block taxonomy).
//
// Community templates are out of scope for v1 per the brief's load-
// bearing constraint #6 — no "Browse community templates →" link.

const FIRST_TIME_DISMISS_KEY = 'templatesPageFirstTimeDismissed';

const BLOCK_TYPES = [
  { key: 'definition', label: 'Definition' },
  { key: 'success_criteria', label: 'Success Criteria' },
  { key: 'required_artifacts', label: 'Required Artifacts' },
  { key: 'verification_method', label: 'Verification Method' },
  { key: 'exclusions', label: 'Exclusions' },
];

export default function Templates() {
  const { workspace } = useAuth();
  const workspaceId = workspace?.id;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFirstTime, setShowFirstTime] = useState(
    () => !localStorage.getItem(FIRST_TIME_DISMISS_KEY)
  );

  const [categoryFilter, setCategoryFilter] = useState('All');
  const [sortBy, setSortBy] = useState('recency');

  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    fetchTemplates();
  }, [workspaceId]);

  async function fetchTemplates() {
    setLoading(true);
    setError(null);
    try {
      const r = await deliverableTemplatesApi.list(workspaceId);
      setTemplates(r.data.templates || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  const categories = useMemo(() => {
    const set = new Set();
    for (const t of templates) {
      if (t.outcome_type_category) set.add(t.outcome_type_category);
    }
    return ['All', ...Array.from(set).sort()];
  }, [templates]);

  const visible = useMemo(() => {
    let list = templates;
    if (categoryFilter !== 'All') {
      list = list.filter((t) => t.outcome_type_category === categoryFilter);
    }
    if (sortBy === 'use_count') {
      list = [...list].sort((a, b) => (b.use_count || 0) - (a.use_count || 0));
    } else {
      list = [...list].sort((a, b) => {
        const at = new Date(a.last_used_at || a.updated_at || a.created_at || 0).getTime();
        const bt = new Date(b.last_used_at || b.updated_at || b.created_at || 0).getTime();
        return bt - at;
      });
    }
    return list;
  }, [templates, categoryFilter, sortBy]);

  function dismissFirstTime() {
    localStorage.setItem(FIRST_TIME_DISMISS_KEY, '1');
    setShowFirstTime(false);
  }

  async function handleArchive(templateId) {
    if (!window.confirm('Archive this template? It will stop showing in the picker for new Proposals.')) {
      return;
    }
    await deliverableTemplatesApi.archive(workspaceId, templateId);
    await fetchTemplates();
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="page-header">Deliverable Templates</h1>
            <p className="text-gray-600 mt-1">
              Reusable starting points. Saved from past Proposals.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="btn-primary text-sm"
          >
            + New template
          </button>
        </div>

        {showFirstTime && (
          <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 flex items-start gap-3">
            <div className="flex-1 text-sm text-primary-900">
              We&rsquo;ve moved your old Outcome Types here as Deliverable
              Templates. Edit, archive, or use them as starting points for
              new Proposals.
            </div>
            <button
              type="button"
              onClick={dismissFirstTime}
              className="text-primary-600 hover:text-primary-800 text-xs"
            >
              Got it
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1.5">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategoryFilter(c)}
                className={`text-xs px-2.5 py-1 rounded-full border transition ${
                  categoryFilter === c
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-1.5 text-xs">
            <span className="text-gray-600">Sort:</span>
            <button
              type="button"
              onClick={() => setSortBy('recency')}
              className={sortBy === 'recency' ? 'font-semibold text-gray-900' : 'text-gray-600 hover:text-gray-800'}
            >
              Recency
            </button>
            <span className="text-gray-300">/</span>
            <button
              type="button"
              onClick={() => setSortBy('use_count')}
              className={sortBy === 'use_count' ? 'font-semibold text-gray-900' : 'text-gray-600 hover:text-gray-800'}
            >
              Use count
            </button>
          </div>
        </div>

        {loading && <p className="text-sm text-gray-600 italic">Loading templates…</p>}
        {error && <p className="text-sm text-rose-600">{error}</p>}

        {!loading && !error && visible.length === 0 && (
          <div className="card p-12 text-center">
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              Save deliverables from your Proposals as templates by clicking
              Save as template on a deliverable. Templates show up here for reuse.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {visible.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              onEdit={() => setEditingTemplate(t)}
              onArchive={() => handleArchive(t.id)}
            />
          ))}
        </div>
      </div>

      {showCreate && (
        <TemplateEditor
          mode="create"
          workspaceId={workspaceId}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); fetchTemplates(); }}
        />
      )}

      {editingTemplate && (
        <TemplateEditor
          mode="edit"
          workspaceId={workspaceId}
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSaved={() => { setEditingTemplate(null); fetchTemplates(); }}
        />
      )}
    </Layout>
  );
}

function TemplateCard({ template, onEdit, onArchive }) {
  return (
    <div className="card p-4 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{template.name}</h3>
          <p className="text-[11px] text-gray-600 mt-0.5">
            {template.outcome_type_category || template.outcome_type_name || 'Uncategorized'}
            {' · '}used {template.use_count || 0}×
          </p>
        </div>
      </div>
      {template.template_spec_seed_json?.definition && (
        <p className="text-xs text-gray-600 line-clamp-3 whitespace-pre-wrap">
          {template.template_spec_seed_json.definition}
        </p>
      )}
      <div className="mt-auto pt-2 flex justify-end gap-2 text-xs">
        <button type="button" onClick={onArchive} className="text-gray-600 hover:text-gray-800">
          Archive
        </button>
        <button type="button" onClick={onEdit} className="text-primary-600 hover:text-primary-800 font-medium">
          Edit
        </button>
      </div>
    </div>
  );
}

function TemplateEditor({ mode, workspaceId, template, onClose, onSaved }) {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [seed, setSeed] = useState(() => {
    const s = template?.template_spec_seed_json || {};
    const out = {};
    for (const b of BLOCK_TYPES) out[b.key] = s[b.key] || '';
    return out;
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  async function handleSave() {
    setSaving(true);
    setErr(null);
    const cleanedSeed = {};
    for (const b of BLOCK_TYPES) {
      if (seed[b.key] && seed[b.key].trim()) cleanedSeed[b.key] = seed[b.key];
    }
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      templateSpecSeed: Object.keys(cleanedSeed).length ? cleanedSeed : null,
    };
    try {
      if (mode === 'create') {
        await deliverableTemplatesApi.create(workspaceId, payload);
      } else {
        await deliverableTemplatesApi.update(workspaceId, template.id, payload);
      }
      onSaved && onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/30 flex justify-center items-start overflow-y-auto" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-white rounded-xl shadow-2xl my-12 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === 'create' ? 'New template' : 'Edit template'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-600 hover:text-gray-700" aria-label="Close"><CloseIcon /></button>
        </header>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="label">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="e.g. Initial Risk Assessment"
            />
          </div>

          <div>
            <label className="label">Description (optional)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input"
              placeholder="One-liner that hints at when to reach for this template"
            />
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Outcome Spec seed (optional)</h3>
            <p className="text-xs text-gray-600">
              These values seed the matching PRD-003 block content as <strong>draft</strong>
              when the template is pulled into a Proposal. Both sides still
              co-author and accept each block before signing.
            </p>
            {BLOCK_TYPES.map((b) => (
              <div key={b.key}>
                <label className="label">{b.label}</label>
                <textarea
                  value={seed[b.key] || ''}
                  onChange={(e) => setSeed((s) => ({ ...s, [b.key]: e.target.value }))}
                  className="input min-h-[60px] font-mono text-xs"
                  placeholder="Markdown content…"
                />
              </div>
            ))}
          </div>

          {err && <p className="text-sm text-rose-600">{err}</p>}
        </div>

        <footer className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="btn-primary text-sm"
          >
            {saving ? 'Saving…' : mode === 'create' ? 'Create template' : 'Save changes'}
          </button>
        </footer>
      </div>
    </div>
  );
}
